import { BorderRadiusMedium, ColorNordicGrey4, Spacing8, Spacing12 } from 'autogen/design-tokens/tokens';
import type {
    ProductTabPropertyListModel,
    ProductTabPropertyListItem,
    PropertyListStylingType
} from 'autogen/swagger/all';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import { rem } from 'modules/helpers/style';
import styled, { css } from 'styled-components';
import PageAction from 'components/shared/Routing/PageAction';

const List = styled.ul`
    list-style: none;
    padding-left: 0;
`;

const ListItem = styled.li`
    display: flex;
    align-items: center;
`;

const LinkElementStyled = styled(PageAction)`
    display: flex;
    align-items: center;
    width: 100%;
`;

const BulletPointBase = css`
    display: inline-block;
    width: ${rem(24)};
    height: ${rem(24)};
    margin-right: ${rem(Spacing8)};
    background-color: ${ColorNordicGrey4};
    border-radius: 100%;
`;

const BulletPointSpan = styled.span`
    ${BulletPointBase}
`;

const ImageBulletPoint = styled(PictureDeprecated)`
    ${BulletPointBase}
    overflow: hidden;
    object-fit: cover;
`;

const StyledContent = styled.div<{ stylingType: PropertyListStylingType }>`
    display: flex;
    align-items: center;
    width: 100%;

    ${({ stylingType }) =>
        stylingType === 'Default'
            ? css`
                  margin-bottom: ${Spacing12};
              `
            : null}

    ${({ stylingType }) =>
        stylingType === 'WithBackground'
            ? css`
                  background-color: ${ColorNordicGrey4};
                  padding: ${Spacing8};
                  border-radius: ${BorderRadiusMedium};
                  font-weight: bold;
                  margin-bottom: ${Spacing8};
              `
            : null}
`;

interface IContent extends ProductTabPropertyListItem {
    stylingType: PropertyListStylingType;
}

function Content({ ...props }: IContent): JSX.Element {
    const { value, image, stylingType } = props;

    return (
        <StyledContent stylingType={stylingType}>
            {image ? <ImageBulletPoint {...image} lazyLoad /> : <BulletPointSpan />}
            {value}
        </StyledContent>
    );
}

export default function PropertyList({ ...props }: ProductTabPropertyListModel): JSX.Element | null {
    const { items, stylingType } = props;
    if (items.length <= 0) return null;

    return (
        <List>
            {items.map((item): JSX.Element => {
                const { value, pageAction } = item;

                return (
                    <ListItem key={value}>
                        {pageAction ? (
                            <LinkElementStyled action={pageAction}>
                                <Content {...item} stylingType={stylingType} />
                            </LinkElementStyled>
                        ) : (
                            <Content {...item} stylingType={stylingType} />
                        )}
                    </ListItem>
                );
            })}
        </List>
    );
}
